import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { Chart, LinearScale, CategoryScale, PointElement, LineElement, TimeScale, Legend, Tooltip } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { CompetitionContext } from '../contexts/CompetitionContext';
import { useTheme } from '../contexts/ThemeContext';
import { fetchCompetitionStatistics } from '../services/CompetitionService';
import CompetitionStatisticsView from '../components/CompetitionStatistics/CompetitionStatisticsView';
import Loading from '../components/Common/Loading';

Chart.register(LinearScale, CategoryScale, PointElement, LineElement, TimeScale, Legend, Tooltip);

const CompetitionStatistics = () => {
    const [stats, setStats] = useState(null);
    const { competition, isLoading, error } = useContext(CompetitionContext);
    const { theme } = useTheme();

    useEffect(() => {
        if (competition) {
            const fetchStats = async () => {
                try {
                    const statistics = await fetchCompetitionStatistics(competition.competitionId);
                    setStats(statistics);

                } catch (error) {
                    console.error('Error fetching competition statistics:', error);
                }
            };
            fetchStats();
        }
    }, [competition]);

    const calculateStatistics = useCallback((competitors, stats) => {
        if (!competitors || !stats || !stats.cumulativePointsTimeseries || !competition.startDate) return [];


        const statistics = Object.entries(stats.cumulativePointsTimeseries).map(([username, activities], index) => {
            const user = competitors.find((competitor) => competitor.username === username)
            const data = {
                label: user.firstName,
                data: activities.sort().map(activity => ({
                    x: activity.date.split('T')[0],
                    y: activity.points
                })),
                borderColor: theme === 'dark'
                    ? `hsl(${360 * index / competitors.length}, 100%, 80%)`
                    : `hsl(${360 * index / competitors.length}, 100%, 50%)`,
                backgroundColor: theme === 'dark'
                    ? `hsla(${360 * index / competitors.length}, 100%, 80%, 0.2)`
                    : `hsla(${360 * index / competitors.length}, 100%, 50%, 0.2)`,
                fill: false,
                tension: 0.2
            };
            return data
        });
        return statistics
    }, [theme, competition]);

    const datasets = useMemo(() => {
        if (!competition || !stats) return [];
        return calculateStatistics(competition.competitors, stats);
    }, [competition, stats, calculateStatistics]);


    const options = useMemo(() => {
        const darkMode = theme === 'dark';

        return {
            scales: {
                x: {
                    type: 'time',
                    time: { unit: 'day', tooltipFormat: 'MMM dd', min: competition?.startDate, max: competition?.endDate },
                    title: { display: true, text: 'Date', color: darkMode ? '#b9b9b9' : '#000000' },
                    ticks: { color: darkMode ? '#b9b9b9' : '#000000' },
                    grid: { color: darkMode ? '#707070' : '#b9b9b9' }
                },
                y: {
                    min: 0,
                    title: { display: true, text: 'Cumulative Points', color: darkMode ? '#b9b9b9' : '#000000' },
                    ticks: { color: darkMode ? '#b9b9b9' : '#000000' },
                    grid: { color: darkMode ? '#707070' : '#b9b9b9' }
                }
            },
            plugins: {
                legend: { display: true, labels: { color: darkMode ? '#b9b9b9' : '#000000' } },
                tooltip: {
                    callbacks: {
                        label: context => `${context.dataset.label}: ${context.raw.y.toFixed(2)} points on ${context.label}`
                    },
                    backgroundColor: darkMode ? '#333' : '#fff',
                    titleColor: darkMode ? '#b9b9b9' : '#000',
                    bodyColor: darkMode ? '#b9b9b9' : '#000'
                }
            }
        };
    }, [competition, theme]);

    if (!stats) {
        return <Loading></Loading>
    }
    return (
        <CompetitionStatisticsView
            competition={competition}
            datasets={datasets}
            totalActivities={stats.totalActivities}
            totalPoints={parseFloat(stats.totalPoints)}
            options={options}
            isLoading={isLoading}
            error={error}
            theme={theme}
        />
    );
};

export default CompetitionStatistics;
